import { useState, useEffect } from 'react';
import apiClient from '@/core/apiClient';

export interface TemplateLineStructure {
  account_id: number;
  debit_formula: string;
  credit_formula: string;
  description: string;
  is_optional?: boolean;
}

export interface AccountingTemplate {
  id: number;
  name: string;
  description: string;
  template_code: string;
  template_structure: TemplateLineStructure[];
  is_active: boolean;
}

export function AccountingTemplates() {
  const [templates, setTemplates] = useState<AccountingTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AccountingTemplate | null>(null);
  const [applyVariables, setApplyVariables] = useState<Record<string, any>>({});
  const [formData, setFormData] = useState({ name: '', template_code: '', description: '' });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/comptabilitat/templates');
      setTemplates(response.data?.data || []);
    } catch (error) {
      console.error('[v0] Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!formData.name || !formData.template_code) {
      alert('El nom i el codi són obligatoris');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/comptabilitat/templates', {
        name: formData.name,
        template_code: formData.template_code,
        description: formData.description,
        template_structure: [],
      });
      setFormData({ name: '', template_code: '', description: '' });
      setShowForm(false);
      loadTemplates();
    } catch (error: any) {
      alert('Error en crear la plantilla: ' + (error?.response?.data?.error || error?.message || 'Error desconegut'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Estàs segur que vols desactivar aquesta plantilla?')) return;
    setLoading(true);
    try {
      await apiClient.delete(`/comptabilitat/templates/${id}`);
      loadTemplates();
    } catch (error: any) {
      alert('Error en desactivar: ' + (error?.message || 'Error desconegut'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const response = await apiClient.post(`/comptabilitat/templates/${selectedTemplate.id}/apply`, {
        variables: applyVariables,
      });
      alert('Plantilla aplicada. Assentament creat: ' + response.data?.data?.id);
      setApplyVariables({});
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('[v0] Error applying template:', error);
      alert('Error en aplicar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Plantilles d&apos;Assentaments</h1>
        <p className="text-gray-600">Gestiona plantilles reutilitzables per automatitzar assentaments</p>
      </div>

      <div className="flex gap-4">
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          disabled={loading}
        >
          + Nova Plantilla
        </button>
      </div>

      {showForm && (
        <div className="border p-4 rounded bg-gray-50 space-y-4">
          <h3 className="font-bold">Nova Plantilla</h3>
          <div>
            <label className="block text-sm font-bold mb-1">Nom *</label>
            <input
              type="text"
              placeholder="Ex: Lloguer Mensual"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Codi de Plantilla *</label>
            <input
              type="text"
              placeholder="Ex: TPL_RENT_001"
              value={formData.template_code}
              onChange={(e) => setFormData({ ...formData, template_code: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Descripció</label>
            <input
              type="text"
              placeholder="Descripció opcional"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateTemplate}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
            >
              Desar
            </button>
            <button
              onClick={() => { setShowForm(false); setFormData({ name: '', template_code: '', description: '' }); }}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel·lar
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {templates.length === 0 ? (
          <p className="text-gray-500">No hi ha plantilles disponibles</p>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="border p-4 rounded hover:bg-gray-50">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-sm">{template.name}</h3>
                  <p className="text-sm text-gray-600">{template.description}</p>
                  <div className="mt-2 text-xs text-gray-500">Codi: {template.template_code}</div>
                  <div className="mt-2 text-xs">
                    {template.template_structure.length} línia(es)
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTemplate(template)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                  >
                    Aplicar
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    Desactivar
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedTemplate && (
        <div className="border p-4 rounded bg-blue-50 space-y-4">
          <h3 className="font-bold text-sm">Aplicar: {selectedTemplate.name}</h3>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-bold mb-1">Quantitat/Import</label>
              <input
                type="number"
                placeholder="1000"
                className="w-full px-3 py-2 border rounded"
                onChange={(e) =>
                  setApplyVariables({ ...applyVariables, amount: parseFloat(e.target.value) })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Descripció</label>
              <input
                type="text"
                placeholder="Descripció de l'assentament"
                className="w-full px-3 py-2 border rounded"
                onChange={(e) =>
                  setApplyVariables({ ...applyVariables, description: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleApplyTemplate}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              disabled={loading}
            >
              Generar Assentament
            </button>
            <button
              onClick={() => setSelectedTemplate(null)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel·lar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
